/**
 * Safe React rendering of Flux Agent Markdown replies. Text from the model is parsed by
 * `fluxAgentMarkdown` into tokens and emitted as React text nodes, so embedded HTML is shown
 * literally and never interpreted. Links open in a new tab without an opener reference.
 *
 * @module apps/web/src/components/flux-agent/FluxAgentMarkdown
 */

import { useMemo } from 'react';
import { parseMarkdown, type MarkdownInline } from '@/lib/fluxAgentMarkdown';
import { cn } from '@/lib/utils';

/** Inputs for {@link FluxAgentMarkdown}. */
export interface FluxAgentMarkdownProps {
  /** Untrusted Markdown text to display. */
  content: string;
  /** Extra classes for the wrapping element. */
  className?: string;
}

/** Render inline tokens as text, emphasis, code, or a safe external link.
 * @component Inline
 */
function Inline({
  tokens,
}: {
  /** Parsed inline runs from one block. */
  tokens: MarkdownInline[];
}) {
  return (
    <>
      {tokens.map((token, index) => {
        switch (token.type) {
          case 'strong':
            return <strong key={index}>{token.text}</strong>;
          case 'em':
            return <em key={index}>{token.text}</em>;
          case 'code':
            return (
              <code key={index} className="bg-muted rounded px-1 py-0.5 font-mono text-[0.85em]">
                {token.text}
              </code>
            );
          case 'link':
            return (
              <a
                key={index}
                href={token.href}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="text-primary underline underline-offset-2"
              >
                {token.text}
              </a>
            );
          default:
            return <span key={index}>{token.text}</span>;
        }
      })}
    </>
  );
}

/** Render a Markdown reply as headings, paragraphs, lists, quotes, code blocks, and rules. */
export function FluxAgentMarkdown({ content, className }: FluxAgentMarkdownProps) {
  const blocks = useMemo(() => parseMarkdown(content), [content]);
  return (
    <div className={cn('space-y-2 break-words text-sm leading-relaxed', className)}>
      {blocks.map((block, index) => {
        switch (block.type) {
          case 'heading':
            return (
              <p
                key={index}
                role="heading"
                aria-level={Math.min(6, block.level + 2)}
                className="pt-1 font-semibold"
              >
                <Inline tokens={block.inline} />
              </p>
            );
          case 'list': {
            const List = block.ordered ? 'ol' : 'ul';
            return (
              <List
                key={index}
                className={cn('space-y-1 pl-5', block.ordered ? 'list-decimal' : 'list-disc')}
              >
                {block.items.map((item, itemIndex) => (
                  <li key={itemIndex}>
                    <Inline tokens={item} />
                  </li>
                ))}
              </List>
            );
          }
          case 'quote':
            return (
              <blockquote
                key={index}
                className="border-primary/40 text-muted-foreground border-l-2 pl-3"
              >
                <Inline tokens={block.inline} />
              </blockquote>
            );
          case 'code':
            return (
              <pre
                key={index}
                className="bg-muted overflow-x-auto rounded-md p-3 font-mono text-xs"
              >
                <code>{block.text}</code>
              </pre>
            );
          case 'rule':
            return <hr key={index} className="border-border" />;
          default:
            return (
              <p key={index}>
                <Inline tokens={block.inline} />
              </p>
            );
        }
      })}
    </div>
  );
}
